"use client";

import { createContext, useCallback, useContext, useMemo, useSyncExternalStore } from "react";
import { cn } from "@/lib/utils";
import type { EventHeader, EventId, LeagueId } from "@/lib/odds-screen/live/types";
import { matchupLabel } from "./labels";
import { NO_STARS, toggleStar, type Stars } from "./starred";

type StarActions = { readonly stars: Stars; readonly toggle: (id: EventId) => void };

export const StarsContext = createContext<StarActions | null>(null);

const listeners = new Set<() => void>();
let held: { readonly key: string; readonly raw: string | null; readonly stars: Stars } | null = null;

function parse(raw: string | null): Stars {
  if (raw === null) return NO_STARS;
  try {
    const ids: unknown = JSON.parse(raw);
    return Array.isArray(ids) && ids.every((id): id is EventId => typeof id === "string") ? new Set(ids) : NO_STARS;
  } catch {
    return NO_STARS;
  }
}

function read(key: string): Stars {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
  }
  if (held?.key === key && held.raw === raw) return held.stars;
  held = { key, raw, stars: parse(raw) };
  return held.stars;
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  window.addEventListener("storage", onChange);
  return () => {
    listeners.delete(onChange);
    window.removeEventListener("storage", onChange);
  };
}

export function useStarredGames(userId: string | undefined): StarActions {
  const key = userId === undefined ? null : `upsider_checklist:${userId}:odds_screen_starred`;
  const stars = useSyncExternalStore(subscribe, () => (key === null ? NO_STARS : read(key)), () => NO_STARS);
  const toggle = useCallback((id: EventId) => {
    if (key === null) return;
    try {
      window.localStorage.setItem(key, JSON.stringify([...toggleStar(read(key), id)]));
    } catch {
    }
    for (const listener of listeners) listener();
  }, [key]);
  return useMemo(() => ({ stars, toggle }), [stars, toggle]);
}

export function useStars(): Stars {
  return useContext(StarsContext)?.stars ?? NO_STARS;
}

export function StarIcon({ on, size = 16, className }: { on: boolean; size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={on ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
    </svg>
  );
}

export function StarButton({ league, event, className }: { league: LeagueId; event: EventHeader | undefined; className?: string }) {
  const actions = useContext(StarsContext);
  if (actions === null || event === undefined) return null;
  const on = actions.stars.has(event.id);
  const matchup = matchupLabel(event, league, "spoken") || "this game";
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={`Star ${matchup}`}
      title={`${on ? "Unstar" : "Star"} ${matchup}`}
      onClick={() => actions.toggle(event.id)}
      className={cn(
        "tap relative flex flex-shrink-0 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 pointer-coarse:before:absolute pointer-coarse:before:-left-2 pointer-coarse:before:-top-2 pointer-coarse:before:bottom-0 pointer-coarse:before:right-0 pointer-coarse:before:content-['']",
        on ? "text-primary" : "text-zinc-500 hover:bg-white/[0.06] hover:text-zinc-300",
        className,
      )}
    >
      <StarIcon on={on} />
    </button>
  );
}
